import './StepToolbar.scss';

import { SendAlt } from '@carbon/icons-react';
import { Button } from '@patternfly/react-core';
import {
  BanIcon,
  BlueprintIcon,
  CheckIcon,
  CodeBranchIcon,
  CompressArrowsAltIcon,
  ExpandArrowsAltIcon,
  PowerOffIcon,
  SyncAltIcon,
  TrashIcon,
} from '@patternfly/react-icons';
import clsx from 'clsx';
import { FunctionComponent, Ref, useContext, useMemo } from 'react';

import { AddStepMode, IDataTestID, IVisualizationNode } from '../../../../models';
import { useSendMessageModal } from '../../../../providers/send-message-modal.provider';
import { SettingsContext } from '../../../../providers/settings.provider';
import { getMoveIcons } from '../../Custom/ContextMenu/get-move-icons.util';
import { useDeleteGroup } from '../../Custom/hooks/delete-group.hook';
import { useDeleteStep } from '../../Custom/hooks/delete-step.hook';
import { useDisableStep } from '../../Custom/hooks/disable-step.hook';
import { useDuplicateStep } from '../../Custom/hooks/duplicate-step.hook';
import { useEnableAllSteps } from '../../Custom/hooks/enable-all-steps.hook';
import { useInsertStep } from '../../Custom/hooks/insert-step.hook';
import { useMoveStep } from '../../Custom/hooks/move-step.hook';
import { useReplaceStep } from '../../Custom/hooks/replace-step.hook';
import { useGraphLayout } from '../../Custom/hooks/use-graph-layout.hook';

interface IStepToolbar extends IDataTestID {
  toolbarRef?: Ref<HTMLDivElement>;
  vizNode: IVisualizationNode;
  className?: string;
  isCollapsed?: boolean;
  /** Toggle node collapse / expand */
  onCollapseToggle?: () => void;
}

export const StepToolbar: FunctionComponent<IStepToolbar> = ({
  toolbarRef,
  vizNode,
  className,
  isCollapsed = false,
  onCollapseToggle,
  'data-testid': dataTestId,
}) => {
  const settingsAdapter = useContext(SettingsContext);
  const sendMessageModal = useSendMessageModal();
  const layout = useGraphLayout();
  const { canHaveSpecialChildren, canBeDisabled, canReplaceStep, canRemoveStep, canRemoveFlow } =
    vizNode.getNodeInteraction();
  const label = vizNode?.getNodeLabel(settingsAdapter.getSettings().nodeLabel);
  const { onInsertStep: onInsertSpecial } = useInsertStep(vizNode, AddStepMode.InsertSpecialChildStep);
  const { onToggleDisableNode, isDisabled } = useDisableStep(vizNode);
  const { areMultipleStepsDisabled, onEnableAllSteps } = useEnableAllSteps();
  const { onReplaceNode } = useReplaceStep(vizNode);
  const { onDeleteStep } = useDeleteStep(vizNode);
  const { onDeleteGroup } = useDeleteGroup(vizNode);
  const { canDuplicate, onDuplicate } = useDuplicateStep(vizNode);
  const { canBeMoved: canMoveBefore, onMoveStep: onMoveBefore } = useMoveStep(vizNode, AddStepMode.PrependStep);
  const { canBeMoved: canMoveAfter, onMoveStep: onMoveAfter } = useMoveStep(vizNode, AddStepMode.AppendStep);

  // Get the appropriate move icons based on layout and node type
  const icons = useMemo(() => getMoveIcons(layout, vizNode), [layout, vizNode]);

  return (
    <div className="step-toolbar-wrapper">
      <div ref={toolbarRef} className={clsx(className, 'step-toolbar')} data-testid={dataTestId}>
        {sendMessageModal && (
          <Button
            icon={<SendAlt size={16} />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-send-message`}
            variant="control"
            title="Send test message"
            onClick={(event) => {
              event.stopPropagation();
              const endpoint = vizNode.data?.path || label || 'endpoint';
              sendMessageModal.openSendMessageModal({
                endpoint,
                title: `Send a test message to ${label || endpoint}`,
              });
            }}
          />
        )}

        {canDuplicate && (
          <Button
            icon={<BlueprintIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-duplicate`}
            variant="control"
            title="Duplicate"
            onClick={async (event) => {
              event.stopPropagation();
              await onDuplicate();
            }}
          />
        )}

        {canMoveBefore && (
          <Button
            icon={icons.moveBefore}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-move-before`}
            variant="control"
            title="Move before"
            onClick={async (event) => {
              event.stopPropagation();
              await onMoveBefore();
            }}
          />
        )}

        {canMoveAfter && (
          <Button
            icon={icons.moveNext}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-move-after`}
            variant="control"
            title="Move after"
            onClick={async (event) => {
              event.stopPropagation();
              await onMoveAfter();
            }}
          />
        )}

        {canHaveSpecialChildren && (
          <Button
            icon={<CodeBranchIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-add-special`}
            variant="control"
            title="Add branch"
            onClick={async (event) => {
              event.stopPropagation();
              await onInsertSpecial();
            }}
          />
        )}

        {canBeDisabled && (
          <Button
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-disable`}
            variant="control"
            title={isDisabled ? 'Enable step' : 'Disable step'}
            onClick={(event) => {
              event.stopPropagation();
              onToggleDisableNode();
            }}
          >
            {isDisabled ? <CheckIcon /> : <BanIcon />}
          </Button>
        )}

        {areMultipleStepsDisabled && (
          <Button
            icon={<PowerOffIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-enable-all`}
            variant="control"
            title="Enable all"
            onClick={(event) => {
              event.stopPropagation();
              onEnableAllSteps();
            }}
          />
        )}

        {canReplaceStep && (
          <Button
            icon={<SyncAltIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-replace`}
            variant="control"
            title="Replace step"
            onClick={async (event) => {
              event.stopPropagation();
              await onReplaceNode();
            }}
          />
        )}

        {onCollapseToggle && (
          <Button
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-collapse`}
            variant="control"
            title={isCollapsed ? 'Expand step' : 'Collapse step'}
            onClick={(event) => {
              event.stopPropagation();
              onCollapseToggle();
            }}
          >
            {isCollapsed ? <ExpandArrowsAltIcon /> : <CompressArrowsAltIcon />}
          </Button>
        )}

        {canRemoveStep && (
          <Button
            icon={<TrashIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-delete`}
            variant="stateful"
            state="attention"
            title="Delete step"
            onClick={async (event) => {
              event.stopPropagation();
              await onDeleteStep();
            }}
          />
        )}

        {canRemoveFlow && (
          <Button
            icon={<TrashIcon />}
            className="step-toolbar__button"
            data-testid={`${label}|step-toolbar-button-delete-group`}
            variant="stateful"
            state="attention"
            title="Delete group"
            onClick={async (event) => {
              event.stopPropagation();
              await onDeleteGroup();
            }}
          />
        )}
      </div>
    </div>
  );
};
